import type { ExternalRouteItem, NavRouteTree } from "./types";

const THINGSBOARD_ROLES: ExternalRouteItem["roles"] = [
  "admin",
  "trader",
  "asset",
  "viewer",
];

/**
 * ThingsBoard entity pages embedded via iframe.
 * Routable for deep-link/refresh (allowWildcard), hidden from sidebar (exclude).
 * Parent URL sync via handleRouteChange; keep in sync with externallyHandledPaths.
 *
 * Nested TB sections use parent prefixes (/entities, /profiles, /resources,
 * /security-settings) because TB emits paths like /entities/devices,
 * /profiles/deviceProfiles, and /resources/widgets-library/widgets-bundles.
 */
export const thingsBoardDeepLinkRoutes: ExternalRouteItem[] = [
  {
    path: "/customers",
    label: "Customers",
    exclude: true,
    isExternal: true,
    srcPath: "/customers",
    roles: THINGSBOARD_ROLES,
    allowWildcard: true,
    breadCrumbHandledExternally: true,
  },
  {
    path: "/entities",
    label: "Entities",
    exclude: true,
    isExternal: true,
    srcPath: "/entities",
    roles: THINGSBOARD_ROLES,
    allowWildcard: true,
    breadCrumbHandledExternally: true,
  },
  {
    path: "/ruleChains",
    label: "Rule Chains",
    exclude: true,
    isExternal: true,
    srcPath: "/ruleChains",
    roles: THINGSBOARD_ROLES,
    allowWildcard: true,
    breadCrumbHandledExternally: true,
  },
  {
    path: "/profiles",
    label: "Profiles",
    exclude: true,
    isExternal: true,
    srcPath: "/profiles",
    roles: THINGSBOARD_ROLES,
    allowWildcard: true,
    breadCrumbHandledExternally: true,
  },
  {
    path: "/resources",
    label: "Resources",
    exclude: true,
    isExternal: true,
    srcPath: "/resources",
    roles: THINGSBOARD_ROLES,
    allowWildcard: true,
    breadCrumbHandledExternally: true,
  },
  {
    path: "/security-settings",
    label: "Security Settings",
    exclude: true,
    isExternal: true,
    srcPath: "/security-settings",
    roles: THINGSBOARD_ROLES,
    allowWildcard: true,
    breadCrumbHandledExternally: true,
  },
  {
    path: "/usage",
    label: "Usage",
    exclude: true,
    isExternal: true,
    srcPath: "/usage",
    roles: THINGSBOARD_ROLES,
    allowWildcard: true,
    breadCrumbHandledExternally: true,
  },
];

/**
 * Config for all navigation routes.
 * Routes, Nav menus and permissions are all derived from this config.
 */
const navRouteTree: NavRouteTree = [
  {
    path: "/profile",
    label: "Profile",
    isExternal: true,
    srcPath: "/profile",
    exclude: true,
    roles: ["admin", "trader", "asset", "viewer", "finance"],
    breadCrumbHandledExternally: true,
  },
  {
    path: "/home",
    label: "Home",
    icon: "HomeIcon",
    isExternal: true,
    srcPath: "/home",
    roles: ["admin", "trader", "asset", "viewer", "finance"],
    breadCrumbHandledExternally: true,
  },
  {
    path: "/settings/home",
    label: "Home Settings",
    exclude: true,
    isExternal: true,
    srcPath: "/settings/home",
    roles: ["admin"],
    breadCrumbHandledExternally: true,
  },
  {
    path: "/dashboards",
    label: "Dashboards",
    icon: "DashboardIcon",
    isExternal: true,
    srcPath: "/dashboards",
    roles: THINGSBOARD_ROLES,
    allowWildcard: true,
    breadCrumbHandledExternally: true,
  },
  ...thingsBoardDeepLinkRoutes,
  {
    path: "/portfolio-overview",
    label: "Portfolio Overview",
    icon: "AssessmentIcon",
    isExternal: true,
    srcPath: "/microservice/portfolio-overview/web/",
    roles: ["admin", "trader", "asset", "viewer"],
  },
  {
    path: "/explorer",
    label: "Explorer",
    icon: "AutoGraphIcon",
    isExternal: false,
    roles: ["admin", "trader", "asset"],
  },
  {
    label: "Trading",
    icon: "CurrencyExchangeIcon",
    roles: ["admin", "trader", "asset", "viewer"],
    children: [
      {
        path: "/trading/trade-hub",
        label: "Trade Hub",
        icon: "AccountBalanceIcon",
        isExternal: false,
        isBeta: true,
        roles: ["admin", "trader", "viewer"],
      },

      {
        path: "/trading/optimisation",
        label: "Optimiser Configuration",
        icon: "SettingsSuggestIcon",
        isExternal: true,
        srcPath: "/microservice/optimiser-ui/web/",
        roles: ["admin", "trader", "viewer"],
      },
      {
        path: "/trading/portfolio-control",
        label: "Portfolio Control",
        icon: "TuneIcon",
        isExternal: true,
        srcPath: "/microservice/portfolio-control/web/",
        roles: ["admin", "trader"],
      },
      {
        path: "/trading/bm-automation",
        label: "BM Automation",
        icon: "EqualizerIcon",
        isExternal: true,
        srcPath: "/microservice/bm-automation/web/",
        roles: ["admin"],
      },
      {
        path: "/trading/ancillary-services",
        label: "Ancillary Services Automation",
        icon: "GraphicEqIcon",
        isExternal: true,
        srcPath: "/microservice/gb-dynamic-automation/web/",
        roles: ["admin", "trader", "viewer"],
      },
      {
        path: "/trading/ptimetabling",
        label: "Parameter Timetabling",
        icon: "UpdateIcon",
        isExternal: true,
        srcPath: "/microservice/parameter-timetabling/web/",
        roles: ["admin", "trader", "viewer"],
      },
    ],
  },
  {
    label: "Markets",
    icon: "SsidChartIcon",
    roles: ["admin", "trader", "viewer"],
    children: [
      {
        path: "/markets/pn",
        label: "Physical Notifications",
        icon: "LabelIcon",
        isExternal: true,
        srcPath: "/microservice/pn-exchange/web/",
        roles: ["admin", "trader", "viewer"],
      },

      {
        path: "/markets/m7",
        label: "EPEX Intraday Market",
        icon: "AccountBalanceIcon",
        isExternal: true,
        srcPath: "/microservice/m7-exchange/web/",
        roles: ["admin", "trader", "viewer"],
      },
    ],
  },
  {
    path: "/scheduling",
    label: "Scheduling",
    icon: "ScheduleIcon",
    isExternal: false,
    roles: ["admin", "trader", "asset", "viewer"],
  },
  {
    label: "Reporting",
    icon: "AssignmentIcon",
    roles: ["admin", "trader", "asset", "finance"],
    children: [
      {
        path: "/reporting/trade-blotter",
        label: "Trade Blotter",
        isExternal: false,
        icon: "GridOnIcon",
        roles: ["admin", "trader", "finance"],
      },
      {
        path: "/reporting/data-exporter",
        label: "Data Exporter",
        icon: "DescriptionIcon",
        isExternal: false,
        roles: ["admin", "trader", "asset", "finance"],
      },
    ],
  },
  {
    label: "Simulators",
    icon: "DonutSmallIcon",
    roles: ["admin", "asset", "trader", "viewer"],
    excludedEnvs: ["prod"],
    children: [
      {
        path: "/simulation/pagerduty",
        label: "PagerDuty",
        icon: "WarningIcon",
        isExternal: false,
        roles: ["admin"],
      },
      {
        path: "/simulation/asset/1",
        label: "SimulationAsset1",
        icon: "BatteryFullIcon",
        isExternal: false,
        roles: ["admin", "asset", "trader", "viewer"],
      },
      {
        path: "/simulation/asset/2",
        label: "Asset2",
        icon: "BatteryFullIcon",
        isExternal: false,
        roles: ["admin", "asset", "trader", "viewer"],
      },
      {
        path: "/simulation/asset/3",
        label: "Asset3",
        icon: "BatteryFullIcon",
        isExternal: false,
        roles: ["admin", "asset", "trader", "viewer"],
      },
    ],
  },
  {
    label: "Administration",
    icon: "AccountBoxIcon",
    roles: ["admin"],
    children: [
      {
        path: "/administration/fault",
        label: "Fault",
        icon: "ErrorOutlineIcon",
        isExternal: false,
        roles: ["admin"],
      },
      {
        path: "/administration/test-component",
        label: "Test Component",
        icon: "WarningIcon",
        isExternal: false,
        excludedEnvs: ["prod"],
        roles: ["admin"],
      },
    ],
  },
];

export default navRouteTree;
